import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import dateFormat from 'dateformat';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Input } from '../../../../../common/Form/Input/Input';
import { TextArea } from '../../../../../common/Form/TextArea/TextArea';
import styles from './EditJobDetails.module.scss';
import { maxMobileSize, validationMessages } from '../../../../../../utils/constants';
import { usePrevious, useWindowSize } from '../../../../../../utils/hooks';
import BaseModal from '../../../../../common/ModalWindows/BaseModal';
import ValidationErrorLabel from '../../../../../common/ValidationErrorLabel/ValidationErrorLabel';

function EditJobDetails(props) {
  const { jobData, state, changeState, updateworkHistory, createWorkHistory, updating } = props;

  const prevUpdating = usePrevious(updating);

  const dataFromCVState = useSelector((globalState) => globalState.cv.worksHistory.response);

  const currentItemId = dataFromCVState?.find(
    (job) =>
      job?.Title === jobData?.Title &&
      job?.CompanyName === jobData?.CompanyName &&
      job?.Description === jobData?.Description
  )?.Id;

  const size = useWindowSize();

  const [initialCheck, setInitialCheck] = useState(true);
  const [commonError, setCommonError] = useState('');
  const { t } = useTranslation();
  const validationMessage = useMemo(() => validationMessages(t), [t]);

  const clickSave = (values) => {
    if (!values?.Id && !currentItemId) {
      createWorkHistory({
        ...values,
        CurrentlyWorkThere: true,
        EndDate: dateFormat(new Date(), 'isoDate'),
        StartDate: dateFormat(new Date(), 'isoDate')
      });
    } else {
      updateworkHistory({ ...values, Id: values.Id || currentItemId, CurrentlyWorkThere: true });
    }
  };

  const validationSchema = yup.object({
    Title: yup.string('Enter title').required(validationMessage.WORK_REQ_TITLE),
    CompanyName: yup.string('Enter Company Name').required(validationMessage.WORK_REQ_COMPANY),
    Description: yup.string('Enter description').required(validationMessage.WORK_REQ_BIOGRAPHY)
  });

  const form = useFormik({
    initialValues: jobData || {},
    validationSchema,
    onSubmit: (values) => clickSave(values)
  });

  useEffect(() => {
    setCommonError('');

    if (!initialCheck && (!form.values.Title || !form.values.CompanyName || !form.values.Description)) {
      setCommonError(validationMessage.WORK_CURRENTLY);
    }

    setInitialCheck(false);
  }, [form.values]);

  useEffect(() => {
    if (prevUpdating === true && updating === false) changeState(false);
  }, [updating]);

  return (
    <BaseModal
      open={state}
      fullscreen={size.width < maxMobileSize}
      closeHandler={() => changeState(false)}
      confirmHandler={form.handleSubmit}
      title={t('dashboard.profile.details.job')}
      pending={updating}
    >
      <div className={styles.jobDetails}>
        <Input
          defaultValue={form.values.Title}
          onChange={(e) => form.setFieldValue('Title', e.target.value)}
          placeholder={t('common.forms.yourrole')}
          id="Title"
          name="Title"
          label={t('common.forms.currenttitle')}
          type="text"
          error={form.errors.Title}
        />
        <Input
          defaultValue={form.values.CompanyName}
          onChange={(e) => form.setFieldValue('CompanyName', e.target.value)}
          placeholder={t('common.forms.company')}
          id="CompanyName"
          name="CompanyName"
          label={t('common.forms.currentcompany')}
          type="text"
          error={form.errors.CompanyName}
        />
        <TextArea
          defaultValue={form.values.Description}
          onChange={(e) => form.setFieldValue('Description', e.target.value)}
          placeholder={t('common.forms.yourbio')}
          id="Description"
          name="Description"
          label={t('common.forms.biography')}
          type="text"
          rows={2}
          column={1}
          error={form.errors.Description}
        />
        {commonError && <ValidationErrorLabel message={commonError} />}
      </div>
    </BaseModal>
  );
}

export default EditJobDetails;
